import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { media } from '@minecraft.themes';
import { FlexDynamic, LinkAsButton, PrimaryButton } from '@minecraft.atoms';
import Button from '@minecraft.button';
import Row from '@minecraft.row';
import Col from '@minecraft.col';
import FormattedTalentUnions from '@minecraft.formatted-talent-unions';
import CloudinaryPhoto from '@minecraft.cloudinary-photo';
import { SCREEN_NAMES, AppType } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import { getEnvironment } from '@minecraft.environment';
import { GetSlugByProfileIdDoc } from '@minecraft.graphql-operations';
import { usePermissions } from '@minecraft.permissions';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { AnchorButton, WithIsPreview } from '../styles/ProfileView';
import ProfileViewContext from '../ProfileViewContext';
import { FEATURE_FLAGS, useFeatureExperiment } from '../../FeaturesAndExperiments';

const DuoLink: React.FunctionComponent<{ href: string }> = ({ href, children, ...restProps }) => (
  <Link to={href} {...restProps}>
    {children}
  </Link>
);

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-bottom: 0.0625rem solid ${(props) => props.theme.colors.primary.ltGrey1};
  margin-bottom: 1.25rem;
  padding-bottom: 0.625rem;
  ${media.md`
    border-bottom: 0;
    margin-bottom: 0;
    padding-bottom: 0;
  `}
`;

const LogoWithTitleContainer = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
`;

const TitleContainerBorder = styled.div<WithIsPreview>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-bottom: ${(props): string =>
    props.isPreview ? '0' : `0.0625rem solid ${props.theme.colors.primary.ltGrey1}`};
`;

export const StyledTitle = styled.h1`
  font-size: 2rem;
  margin-bottom: 0.2em;
  word-break: break-all;
`;

export const StyledRepName = styled.h2`
  font-size: 1rem;
  font-weight: 600;
`;

export const StyledSubTitle = styled.h2`
  font-size: 0.85rem;
  font-weight: bold;
`;

const StyledRow = styled(Row)`
  justify-content: space-between;
  flex-direction: column-reverse;
  ${media.md`
    flex-direction: row;
  `}
`;

export const LinkButton = styled(Button)``;

const StyledCloudinaryPhoto = styled(CloudinaryPhoto)`
  margin: 1rem 1rem 0.6875rem 1rem;
  height: 2.5rem;
  max-width: 5.25rem;
  object-fit: contain;
  ${media.sm`
  margin: 0 1rem;
    height: 3.625rem;
    max-width: 7.625rem;
  `}
`;

interface PreviewButtonsProps {
  appType: AppType;
  copyLinkToClipBoard: () => void;
  handleGoBack: () => void;
  isAdmin: boolean;
  isPreview: boolean;
  profileId: string;
  showExit: boolean;
}

const PreviewButtons = ({
  appType,
  copyLinkToClipBoard,
  handleGoBack,
  isAdmin,
  isPreview,
  profileId,
  showExit,
}: PreviewButtonsProps) => {
  const { t } = useTranslation();
  const { push } = useHistory();

  if (!isPreview && isAdmin) {
    return null;
  }

  return (
    <FlexDynamic
      grow={0}
      className="cn_atom_px-4"
      my="2"
      alignItems="flex-end"
      justifyContent="center"
      mdProps={{ my: '0', direction: 'column', className: '' }}
      lgProps={{ direction: 'row', alignItems: 'flex-start' }}
      direction="row"
      wrap="wrap"
      gap="2"
      data-testid="preview-buttons-wrapper"
    >
      {isPreview && showExit && (
        <LinkAsButton
          onClick={handleGoBack}
          data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-exit-preview-link`}
          label={t('common:profile.exitPreview')}
        />
      )}
      {!isPreview && (
        <PrimaryButton
          variant="secondary"
          onClick={() => push(`/profile/${profileId}/preview`)}
          data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-preview-button`}
          label={t('common:profile.button.preview')}
          isSmall
        />
      )}
      {(appType === AppType.Talent || appType === AppType.Reps) && (
        <PrimaryButton
          variant="secondary"
          onClick={copyLinkToClipBoard}
          data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-copy-link`}
          label={t('common:profile.copyProfileLink')}
          isSmall
        />
      )}
    </FlexDynamic>
  );
};

const ProfileHeader = (): JSX.Element => {
  const { t } = useTranslation();
  const environment = getEnvironment();
  const router = useHistory();
  const { ability } = usePermissions();
  const customURLFlag = useFeatureExperiment(FEATURE_FLAGS.WEB_CUSTOM_URL);
  const canEditCustomURL = ability.can('write', 'custom-url');

  const {
    fetcher: { profile },
    isPreview,
    isAdmin,
    onEditClick,
    hidePreviewButtons,
    appType,
    noExit,
  } = React.useContext(ProfileViewContext);

  const { showMessage } = useGlobalNotification();

  const { data: existingSlugData } = useQuery(GetSlugByProfileIdDoc, {
    variables: { profileId: Number(profile.profileId), includeExpired: false },
    skip: !customURLFlag || !canEditCustomURL,
  });

  const customUrlSlugOrProfileGuid = existingSlugData?.getSlugByProfileId?.slugName || profile?.guid;

  const copyToClipBoard = async (): Promise<void> => {
    if (navigator) {
      try {
        const url = `${environment.TALENT_PROFILE_PUBLIC_LINK}/${customUrlSlugOrProfileGuid}`;

        await navigator.clipboard.writeText(url);
        const toastMessage =
          appType === AppType.Talent ? t('common:profile.copiedToClipboard') : t('reps:profile.copiedToClipboard');
        showMessage({ message: toastMessage, color: 'success' });
      } catch {
        console.error('talent public profile link was NOT copied to clipboard');
      }
    }
  };

  const handleGoBack = (): void => router.goBack();

  const getRepName = (): string => {
    const { name, parentName } = profile.profileMainDivision || {};

    return name ? `${parentName} - ${name}` : parentName;
  };

  return (
    <StyledRow data-testid={`profile-header-wrapper-${existingSlugData ? 'with-slug' : 'without-slug'}`}>
      <LogoWithTitleContainer data-testid="profile-stg-union-wrap">
        {profile.profileMainDivision?.logo && (
          <StyledCloudinaryPhoto fileKey={profile.profileMainDivision.logo.fileKey} customSize={[122, 58]} />
        )}
        <Col xs="12" sm="8">
          <TitleContainerBorder data-testid="profile-stage-name-wrapper" isPreview={isPreview}>
            <StyledTitle data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-stage-name`}>{profile.stageName}</StyledTitle>
            {!isPreview && (
              <>
                {isAdmin ? (
                  <AnchorButton
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-stage-name-edit`}
                    onClick={() => {
                      onEditClick('stage-name');
                    }}
                  >
                    {t('common:button.edit')}
                  </AnchorButton>
                ) : (
                  <DuoLink
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-stage-name-edit`}
                    href={`/profile/${profile.profileId}/stage-name`}
                  >
                    {t('common:button.edit')}
                  </DuoLink>
                )}
              </>
            )}
          </TitleContainerBorder>

          <TitleContainer data-qa-id="rep-agency">
            {isPreview && getRepName() && <StyledRepName>{getRepName()}</StyledRepName>}
          </TitleContainer>

          <TitleContainer data-testid="profile-unions-wrapper">
            <StyledSubTitle data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-unions`}>
              {profile.talentUnions.length > 0 ? (
                <FormattedTalentUnions profileId={profile.profileId} unionsList={profile.talentUnions} noTruncation />
              ) : (
                (!isPreview && t('common:profile.unionNotSelected')) || (isPreview && t('common:profile.nonUnion'))
              )}
            </StyledSubTitle>
            {!isPreview && (
              <>
                {isAdmin ? (
                  <AnchorButton
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-unions-edit`}
                    onClick={() => {
                      onEditClick('unions');
                    }}
                  >
                    {t('common:button.edit')}
                  </AnchorButton>
                ) : (
                  <DuoLink
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-unions-edit`}
                    href={`/profile/${profile.profileId}/unions`}
                  >
                    {t('common:button.edit')}
                  </DuoLink>
                )}
              </>
            )}
          </TitleContainer>
        </Col>
      </LogoWithTitleContainer>

      {!hidePreviewButtons && (
        <PreviewButtons
          appType={appType}
          copyLinkToClipBoard={copyToClipBoard}
          handleGoBack={handleGoBack}
          isAdmin={isAdmin}
          isPreview={isPreview}
          profileId={profile?.profileId?.toString()}
          showExit={!noExit}
        />
      )}
    </StyledRow>
  );
};

export default ProfileHeader;
